import { Reveal, Overline } from "../components/motion";
import { MANIFESTO, STATS, IMAGES, GALLERY } from "../data/content";
import { useRef, useState, useEffect } from "react";
import { useInView, animate, motion, useScroll, useTransform } from "framer-motion";
import { EASE } from "../components/motion";

const Counter = ({ value }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, { duration: 2.2, ease: EASE, onUpdate: (v) => setDisplay(Math.round(v)) });
    return () => controls.stop();
  }, [inView, value]);
  return <span ref={ref}>{display.toLocaleString()}</span>;
};

export default function AboutPage() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <main className="pt-20">
      <section className="py-20 md:py-28 bg-navy text-primary-foreground overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Overline light>About the College</Overline>
            <h1 className="mt-4 font-serif text-5xl md:text-6xl tracking-tight leading-[1.05]">
              Adventures in learning.
              <br />
              <span className="italic text-gold">Expertise in life.</span>
            </h1>
          </div>
          <p className="text-primary-foreground/60 leading-relaxed text-base md:text-lg">
            Victoria Career College is a private post-secondary training institution in
            Surrey, British Columbia — certified by the Private Training Institutions
            Regulatory Unit (PTIRU) of the Government of British Columbia.
          </p>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5" ref={imgRef}>
            <div className="sticky top-32 relative">
              <div className="absolute -top-4 -left-4 w-full h-full border border-gold/50 rounded-sm pointer-events-none" />
              <div className="overflow-hidden rounded-sm aspect-[4/5]">
                <motion.img src={IMAGES.workshop} alt="College workshop" style={{ y, scale: 1.12 }} className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 space-y-10">
            <Reveal>
              <Overline>Who We Are</Overline>
              <p className="mt-5 text-lg leading-relaxed text-navy/85 font-serif">
                Our programs are designed for people who want to build real skill — whether
                pursuing a new career or advancing their competence as serious practitioners.
              </p>
              <p className="mt-5 text-muted-foreground leading-relaxed">
                Training is delivered in small, focused classes with a strong emphasis on
                hands-on learning, practical problem-solving, and industry standards. Flexible
                scheduling allows students to train, work, and live while completing an
                education that is formally accredited and respected within the industry.
              </p>
              <p className="mt-5 text-muted-foreground leading-relaxed">
                Our approach is straightforward: teach the craft properly, support learners at
                every stage, and uphold the standards that professionals are expected to meet.
              </p>
            </Reveal>

            <div className="space-y-0 divide-y divide-border border-y border-border">
              {MANIFESTO.map((ch, i) => (
                <Reveal key={ch.number} delay={i * 0.06}>
                  <div className="flex gap-8 items-start py-8 group">
                    <span className="font-serif text-6xl leading-none text-accent/30 transition-colors duration-500 group-hover:text-accent/60 select-none">
                      {ch.number}
                    </span>
                    <div>
                      <h3 className="font-serif text-2xl text-navy">{ch.title}</h3>
                      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{ch.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-secondary/30 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4 gap-y-14">
          {STATS.map((s, i) => (
            <Reveal key={s.label} delay={i * 0.1} className={`px-6 ${i > 0 ? "lg:border-l lg:border-border" : ""}`}>
              <p className="font-serif text-5xl md:text-6xl text-navy">
                <Counter value={s.value} />
                {s.value >= 1000 && <span className="text-accent">+</span>}
              </p>
              <p className="mt-3 text-xs tracking-[0.2em] uppercase text-muted-foreground">{s.label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="py-24 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Overline>The Campus & The Craft</Overline>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-3 gap-6">
            {GALLERY.slice(0, 3).map((g, i) => (
              <Reveal key={g.src} delay={i * 0.1}>
                <div className="group relative overflow-hidden rounded-sm">
                  <img
                    src={g.src}
                    alt={g.caption}
                    data-testid={`about-gallery-${i}`}
                    className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
                  />
                  <p className="absolute bottom-3 left-4 font-serif italic text-lg text-primary-foreground drop-shadow">{g.caption}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <Overline>Accreditation</Overline>
            <p className="mt-6 font-serif text-2xl md:text-3xl leading-relaxed text-navy/90 italic">
              “Victoria Career College is a RESP-eligible training provider and can provide
              form T2202 Education Expense receipts for tax returns. Funding may be available
              for students gaining employment or upgrading their skills.”
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
